// Amenity model/render helpers: low-poly building kit, thumbnails, LOD impostors and measured footprints.
import * as THREE from 'three';
import { bus } from '../core/bus.js';
import { AMENITIES } from './amenityCatalog.js';
import { bakeToSingleMesh } from '../core/meshUtils.js';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { makeBuildingMesh, getBakedModel, getLamp, getCrate, getVase } from '../world/buildingKit.js';
import { buildPalmGeo, buildPineGeo, buildOakGeo } from '../world/environment.js';
import { getBuildingSink } from '../world/buildingConfig.js';

const _CLAY = [0xc96a4a, 0xd98f6a, 0xb5794a, 0xcaa06a, 0x9c6a4a];
const _lampMat = new THREE.MeshStandardMaterial({ color: 0x4a4038, metalness: 0.6, roughness: 0.5 });
function lampMesh(targetH) { const l = getLamp(2); if (!l) return null; const m = new THREE.Mesh(l.geo, _lampMat); m.scale.setScalar(targetH / l.h); m.castShadow = true; return m; }
const _crateMat = new THREE.MeshStandardMaterial({ color: 0xb5824a, roughness: 0.9 });
function crateMesh(idx, targetH) { const c = getCrate(idx); if (!c) return null; const m = new THREE.Mesh(c.geo, _crateMat); m.scale.setScalar(targetH / c.h); m.castShadow = true; return m; }
let _tileTex = null;
function tileMat(rw, rh) {
  if (!_tileTex) {
    const cv = document.createElement('canvas'); cv.width = cv.height = 128;
    const x = cv.getContext('2d');
    x.fillStyle = '#cdb896'; x.fillRect(0, 0, 128, 128);
    x.strokeStyle = 'rgba(92,70,45,0.45)'; x.lineWidth = 5;
    for (let i = 0; i <= 128; i += 64) { x.beginPath(); x.moveTo(i, 0); x.lineTo(i, 128); x.moveTo(0, i); x.lineTo(128, i); x.stroke(); }
    for (let i = 0; i < 200; i++) { x.fillStyle = Math.random() < 0.5 ? 'rgba(150,124,84,0.25)' : 'rgba(240,228,196,0.25)'; x.fillRect(Math.random() * 128, Math.random() * 128, 2, 2); }
    _tileTex = new THREE.CanvasTexture(cv); _tileTex.colorSpace = THREE.SRGBColorSpace;
  }
  const tex = _tileTex.clone(); tex.needsUpdate = true; tex.wrapS = tex.wrapT = THREE.RepeatWrapping; tex.repeat.set(rw, rh);
  return new THREE.MeshStandardMaterial({ map: tex, roughness: 0.96 });
}
function vaseMesh(idx, targetH) { const v = getVase(idx); if (!v) return null; const m = new THREE.Mesh(v.geo, new THREE.MeshStandardMaterial({ color: _CLAY[((idx % 5) + 5) % 5], roughness: 0.85 })); m.scale.setScalar(targetH / v.h); m.castShadow = true; return m; }
let _potPalmGeo = null;
const _potPalmMat = new THREE.MeshStandardMaterial({ vertexColors: true, flatShading: true, roughness: 0.9 });
function potPalm() { if (!_potPalmGeo) _potPalmGeo = buildPalmGeo(); const m = new THREE.Mesh(_potPalmGeo, _potPalmMat); m.castShadow = true; return m; }
const _POT = {};
function potTreeMesh(kind) { if (!_POT[kind]) _POT[kind] = kind === 'pine' ? buildPineGeo() : kind === 'oak' ? buildOakGeo() : buildPalmGeo(); const m = new THREE.Mesh(_POT[kind], _potPalmMat); m.castShadow = true; return m; }

/* ---------- tiny model kit ---------- */
const MATS = new Map();
function mat(c, rough = 0.85) {
  const key = c + '|' + rough;
  if (!MATS.has(key)) MATS.set(key, new THREE.MeshStandardMaterial({ color: c, roughness: rough, flatShading: true }));
  return MATS.get(key);
}
function M(geo, c, rough) {
  const m = new THREE.Mesh(geo, mat(c, rough));
  m.castShadow = true;
  return m;
}
const box = (w, h, d, x = 0, y = 0, z = 0) => { const g = new THREE.BoxGeometry(w, h, d); g.translate(x, y + h / 2, z); return g; };
const cyl = (rt, rb, h, s, x = 0, y = 0, z = 0) => { const g = new THREE.CylinderGeometry(rt, rb, h, s); g.translate(x, y + h / 2, z); return g; };
const cone = (r, h, s, x = 0, y = 0, z = 0) => { const g = new THREE.ConeGeometry(r, h, s); g.translate(x, y + h / 2, z); return g; };
const ball = (r, x = 0, y = 0, z = 0, dt = 0) => { const g = new THREE.IcosahedronGeometry(r, dt); g.translate(x, y, z); return g; };
// gabled roof (triangular prism) spanning w x d, ridge along x
// Correct gabled-roof prism: triangle (base w, height h) extruded over depth d, ridge along x.
const gable = (w, d, h, x = 0, y = 0, z = 0) => {
  const sh = new THREE.Shape();
  sh.moveTo(-d / 2, 0); sh.lineTo(d / 2, 0); sh.lineTo(0, h); sh.closePath();
  const g = new THREE.ExtrudeGeometry(sh, { depth: w, bevelEnabled: false, steps: 1 });
  g.rotateY(Math.PI / 2);          // extrude axis (z) -> x
  g.translate(x - w / 2, y, z);    // center the width and place
  return g;
};
// ===== SAFARI LODGE KIT — every amenity is a real thatched-roof building =====
const WOOD = 0x9c8468, WOOD_D = 0x7a5c3a, WHITE = 0xfdf6ea, GLASS = 0xbfe3ea, STONE = 0xe9e3d7;
// wall tones (warm safari earth) + shared thatch tones
const ADOBE = 0xe7c58a, ADOBE_D = 0xd0a566, PLASTER = 0xf0e2c2, CLAY = 0xd98f6a, TEAK = 0xa77e4e;
const THATCH = 0xcaa35a, THATCH2 = 0xb98d44, THATCH_D = 0x9c7636, RIDGE = 0x7f5f30, BAMBOO = 0xc9b26a;

// Ragged straw fringe hanging from an eave line (row of tiny drooping cones).
function fringe(g, x0, x1, z, y, col = THATCH_D) {
  const n = Math.max(3, Math.round(Math.abs(x1 - x0) / 0.22));
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    const c = cone(0.06, 0.16 + (i % 2) * 0.05, 4, x0 + (x1 - x0) * t, y, z);
    c.rotateX(Math.PI);
    g.add(M(c, col, 0.9));
  }
}
// Layered THATCHED GABLE roof over a w x d building; ridge runs along x.
function thatchGable(g, w, d, h, x, y, z) {
  const over = 0.32;
  g.add(M(gable(w + over, d + over, h, x, y, z), THATCH, 0.95));                    // main thatch
  g.add(M(gable(w + over * 0.5, d + over, h * 0.5, x, y + h * 0.5, z), THATCH2, 0.95)); // upper tier (thatch step)
  g.add(M(box(w + over, 0.12, 0.18, x, y + h - 0.03, z), RIDGE, 0.9));              // ridge cap
  fringe(g, x - (w + over) / 2, x + (w + over) / 2, z + (d + over) / 2, y + 0.05);  // eave fringe front
  fringe(g, x - (w + over) / 2, x + (w + over) / 2, z - (d + over) / 2, y + 0.05);  // eave fringe back
}
// Conical THATCHED roof for round huts.
function thatchCone(g, r, h, x, y, z) {
  g.add(M(cone(r + 0.22, h, 14, x, y, z), THATCH, 0.95));
  g.add(M(cone(r * 0.7 + 0.14, h * 0.62, 14, x, y + h * 0.36, z), THATCH2, 0.95));
  g.add(M(ball(0.11, x, y + h + 0.02, z), RIDGE));
  const n = 18;
  for (let i = 0; i < n; i++) {
    const a = i / n * Math.PI * 2;
    const c = cone(0.055, 0.18, 4, x + Math.cos(a) * (r + 0.2), y + 0.03, z + Math.sin(a) * (r + 0.2));
    c.rotateX(Math.PI);
    g.add(M(c, THATCH_D, 0.9));
  }
}
// Bamboo/timber corner posts around a footprint.
function posts(g, W, D, h = 1.9, col = BAMBOO) {
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
    g.add(M(cyl(0.06, 0.075, h, 6, sx * W * 0.46, 0, sz * D * 0.46), col));
    g.add(M(ball(0.07, sx * W * 0.46, h, sz * D * 0.46), col));
  }
}
// A serving window: dark opening + timber sill + counter shelf on the +z face.
function servingWindow(g, W, D, wallCol) {
  g.add(M(box(W * 0.5, 0.5, 0.06, 0, 0.62, D * 0.5), 0x2a2018));       // opening
  g.add(M(box(W * 0.6, 0.09, 0.26, 0, 0.56, D * 0.5 + 0.06), TEAK));   // counter shelf
  g.add(M(box(W * 0.6, 0.06, 0.1, 0, 1.12, D * 0.5), WOOD_D));         // lintel
  for (const sx of [-1, 1]) g.add(M(box(0.06, 0.55, 0.1, sx * W * 0.26, 0.6, D * 0.5), WOOD_D));
}
// A hanging sign board on posts in front of the building.
function signBoard(g, W, D, col, y = 1.5) {
  g.add(M(cyl(0.04, 0.04, y + 0.3, 5, -W * 0.34, 0, D * 0.55), WOOD_D));
  g.add(M(cyl(0.04, 0.04, y + 0.3, 5, W * 0.34, 0, D * 0.55), WOOD_D));
  g.add(M(box(W * 0.72, 0.05, 0.06, 0, y + 0.28, D * 0.55), WOOD_D));
  g.add(M(box(W * 0.6, 0.34, 0.05, 0, y - 0.05, D * 0.55), col, 0.6));
  g.add(M(box(W * 0.44, 0.06, 0.06, 0, y - 0.05, D * 0.552), WHITE));  // painted stripe
}

/* ---------- unique builders per type (all real safari huts) ---------- */
const BUILDERS = {
  food(def, W, D, g) {
    g.add(M(box(W * 0.86, 1.35, D * 0.78, 0, 0, -D * 0.05), ADOBE));       // adobe walls
    g.add(M(box(W * 0.9, 0.1, D * 0.82, 0, 1.35, -D * 0.05), ADOBE_D));    // wall cap
    thatchGable(g, W * 0.92, D * 0.86, 0.95, 0, 1.6, -D * 0.05);
    servingWindow(g, W, D, ADOBE);
    posts(g, W, D, 1.4);
    g.add(M(box(0.16, 0.55, 0.16, W * 0.34, 1.45, -D * 0.32), 0x8f8577));  // chimney
    g.add(M(ball(0.1, W * 0.34, 2.15, -D * 0.32), 0xeeeae2));              // smoke
    g.add(M(cyl(0.1, 0.12, 0.2, 8, -0.3, 0.66, D * 0.5), 0xf2a45c));       // soup pot on counter
    g.add(M(ball(0.08, 0.28, 0.7, D * 0.5), 0xd35f3c));                     // tomato
    signBoard(g, W, D, def.color, 1.55);
  },
  pizza(def, W, D, g) {
    g.add(M(box(W * 0.84, 1.4, D * 0.78, 0, 0, -D * 0.04), CLAY));
    thatchGable(g, W * 0.9, D * 0.86, 1.0, 0, 1.62, -D * 0.04);
    servingWindow(g, W, D, CLAY);
    // domed stone oven bumping out the side
    g.add(M(ball(0.5, -W * 0.5, 0.5, -D * 0.05), STONE));
    g.add(M(box(0.34, 0.26, 0.08, -W * 0.5, 0.34, D * 0.34), 0x3a2a20));
    g.add(M(cyl(0.08, 0.08, 0.6, 6, -W * 0.5, 1.0, -D * 0.05), 0x8f8577));
    g.add(M(ball(0.11, -W * 0.5, 1.7, -D * 0.05), 0xeeeae2));
    posts(g, W, D, 1.45);
    signBoard(g, W, D, def.color, 1.6);
    g.add(M(ball(0.05, 0, 1.28, D * 0.552), 0xf5d98a)); // little pizza dot on sign
  },
  candy(def, W, D, g) {
    // round pastel roundhut
    g.add(M(cyl(W * 0.42, W * 0.44, 1.15, 12), 0xf3c9de));
    g.add(M(cyl(W * 0.44, W * 0.44, 0.12, 12, 0, 1.15, 0), 0xe89ac0));
    thatchCone(g, W * 0.46, 0.9, 0, 1.27, 0);
    g.add(M(box(0.34, 0.7, 0.06, 0, 0.1, W * 0.42), WOOD_D));               // door
    // candy jars in a side window
    g.add(M(box(0.5, 0.4, 0.06, -W * 0.28, 0.62, W * 0.34), 0x2a2018));
    for (let i = 0; i < 3; i++) g.add(M(cyl(0.07, 0.07, 0.18, 8, -W * 0.4 + i * 0.12, 0.62, W * 0.37), [0xe86a6a, 0x8ecbe0, 0xf5d98a][i], 0.4));
    g.add(M(ball(0.14, 0, 2.3, 0), 0xd66a9e)); // candy topper (under cone tip already)
  },
  drink(def, W, D, g) {
    // "watering hole" bamboo bar hut
    g.add(M(box(W * 0.86, 1.2, D * 0.7, 0, 0, -D * 0.08), TEAK));
    thatchGable(g, W * 0.94, D * 0.82, 0.9, 0, 1.42, -D * 0.08);
    servingWindow(g, W, D, TEAK);
    // bamboo bar front posts
    for (const sx of [-1, 1]) g.add(M(cyl(0.07, 0.08, 1.6, 6, sx * W * 0.42, 0, D * 0.42), BAMBOO));
    g.add(M(box(W * 0.9, 0.08, 0.12, 0, 1.55, D * 0.42), BAMBOO));
    // bottle shelf
    for (let i = 0; i < 4; i++) g.add(M(cyl(0.045, 0.05, 0.22, 6, -0.34 + i * 0.22, 0.66, D * 0.5), [0xe86a6a, 0xf5d98a, 0x8fd88f, 0x8ecbe0][i], 0.4));
    signBoard(g, W, D, def.color, 1.4);
  },
  coffee(def, W, D, g) {
    // timber lodge with a covered veranda (2x1)
    g.add(M(box(W * 0.56, 1.5, D * 0.82, -W * 0.16, 0, 0), 0x6a4a32));
    thatchGable(g, W * 0.64, D * 0.9, 1.0, -W * 0.16, 1.72, 0);
    g.add(M(box(0.32, 0.85, 0.05, -W * 0.16, 0.1, D * 0.42), WOOD_D));      // door
    g.add(M(box(0.3, 0.34, 0.05, -W * 0.38, 0.7, D * 0.42), GLASS));        // window
    g.add(M(box(0.16, 0.5, 0.16, -W * 0.02, 1.9, -D * 0.2), 0xc9b8a8));     // chimney
    // veranda
    g.add(M(box(W * 0.36, 0.1, D * 0.82, W * 0.28, 0, 0), 0xcbb994));
    for (const sz of [-1, 1]) g.add(M(cyl(0.06, 0.07, 1.6, 6, W * 0.44, 0.1, sz * D * 0.36), BAMBOO));
    g.add(M(box(W * 0.42, 0.08, D * 0.86, W * 0.3, 1.6, 0), THATCH_D));     // veranda roof beam
    thatchGable(g, W * 0.4, D * 0.9, 0.5, W * 0.3, 1.72, 0);
    // café table
    g.add(M(cyl(0.03, 0.04, 0.42, 6, W * 0.3, 0.1, 0), WOOD_D));
    g.add(M(cyl(0.17, 0.17, 0.05, 10, W * 0.3, 0.5, 0), WHITE));
    g.add(M(cyl(0.09, 0.09, 0.14, 8, W * 0.13, 0.1, D * 0.22), TEAK));      // stool
    signBoard(g, W, D, def.color, 1.6);
  },
  icecream(def, W, D, g) {
    g.add(M(cyl(W * 0.42, W * 0.44, 1.1, 12), 0xbfe6ea));
    thatchCone(g, W * 0.46, 0.85, 0, 1.22, 0);
    servingWindow(g, W * 0.9, D * 0.9, 0xbfe6ea);
    // giant cone topper (poke through the thatch tip)
    const cn = cone(0.16, 0.44, 7, 0, 2.25, 0); cn.rotateX(Math.PI);
    g.add(M(cn, 0xd9a05a));
    g.add(M(ball(0.14, 0, 2.72, 0), 0xf8e6f0));
    g.add(M(ball(0.12, 0.05, 2.9, 0), 0x8ec9d6));
    g.add(M(ball(0.045, 0, 3.0, 0), 0xe86a6a));
  },
  toilet(def, W, D, g) {
    g.add(M(box(W * 0.72, 1.5, D * 0.68, 0, 0, 0), ADOBE));
    thatchGable(g, W * 0.8, D * 0.78, 0.6, 0, 1.66, 0);
    g.add(M(box(0.3, 0.9, 0.05, 0, 0.1, D * 0.35), WOOD_D));                // door
    g.add(M(box(0.18, 0.14, 0.05, 0, 1.05, D * 0.36), 0x8ecbe0));           // WC sign
    g.add(M(cyl(0.05, 0.05, 0.35, 6, W * 0.24, 1.85, -D * 0.1), 0x8f8577)); // vent
    posts(g, W, D, 1.5);
  },
  restroom(def, W, D, g) {
    g.add(M(box(W * 0.88, 1.5, D * 0.66, 0, 0, 0), ADOBE_D));
    g.add(M(box(W * 0.92, 0.1, D * 0.72, 0, 1.5, 0), PLASTER));
    thatchGable(g, W * 0.96, D * 0.78, 0.62, 0, 1.72, 0);
    for (const sx of [-1, 1]) {
      g.add(M(box(0.3, 0.9, 0.05, sx * W * 0.24, 0.1, D * 0.34), WOOD_D));
      g.add(M(box(0.16, 0.14, 0.05, sx * W * 0.24, 1.05, D * 0.345), sx < 0 ? 0x8ecbe0 : 0xf2a4b8));
    }
    g.add(M(box(0.55, 0.26, 0.26, 0, 0.1, D * 0.44), 0xb28a62));            // planter
    g.add(M(ball(0.14, -0.12, 0.42, D * 0.44), 0x8fd88f));
    g.add(M(ball(0.12, 0.13, 0.4, D * 0.44), 0x7fca82));
    posts(g, W, D, 1.5);
  },
  info(def, W, D, g) {
    g.add(M(cyl(0.6, 0.66, 1.5, 8), TEAK));
    g.add(M(cyl(0.72, 0.72, 0.1, 8, 0, 1.5, 0), WOOD_D));
    thatchCone(g, 0.8, 0.8, 0, 1.6, 0);
    g.add(M(box(0.42, 0.55, 0.05, 0, 0.6, 0.62), GLASS));                   // window
    // big "i" totem out front
    g.add(M(cyl(0.05, 0.05, 1.5, 6, 0, 0, D * 0.6), WOOD));
    g.add(M(box(0.28, 0.36, 0.06, 0, 1.55, D * 0.6), 0x3c7fd3, 0.5));
    g.add(M(box(0.07, 0.16, 0.07, 0, 1.6, D * 0.6 + 0.005), WHITE));
    g.add(M(ball(0.045, 0, 1.82, D * 0.6), WHITE));
    // map board on the side
    g.add(M(box(0.5, 0.4, 0.05, W * 0.42, 0.7, 0), 0x4a8a6a));
  },
  balloon(def, W, D, g) {
    // small kiosk hut + balloon bunch tied to it
    g.add(M(cyl(0.5, 0.54, 1.1, 10), 0xf0d98a));
    thatchCone(g, 0.6, 0.7, 0, 1.2, 0);
    g.add(M(box(0.36, 0.5, 0.05, 0, 0.5, 0.52), 0x2a2018));                 // window
    g.add(M(cyl(0.03, 0.03, 1.7, 5, W * 0.36, 0.9, D * 0.2), WOOD));
    const cols = [0xe86a6a, 0xf5d98a, 0x8fd88f, 0x8ecbe0, 0xb7a8e8, 0xf2a4b8];
    for (let i = 0; i < 6; i++) {
      const a = i / 6 * Math.PI * 2;
      const bx = W * 0.36 + Math.cos(a) * 0.3, bz = D * 0.2 + Math.sin(a) * 0.3;
      const by = 2.55 + Math.sin(i * 2.1) * 0.2;
      g.add(M(ball(0.16, bx, by, bz), cols[i], 0.35));
    }
  },
  shop(def, W, D, g) {
    // Trading Post — big timber lodge with veranda (2x2)
    g.add(M(box(W * 0.82, 1.8, D * 0.72, 0, 0, -D * 0.06), TEAK));
    g.add(M(box(W * 0.86, 0.12, D * 0.76, 0, 1.8, -D * 0.06), WOOD_D));
    thatchGable(g, W * 0.9, D * 0.82, 1.1, 0, 2.05, -D * 0.06);
    g.add(M(box(0.5, 0.95, 0.06, 0, 0.1, D * 0.3), WOOD_D));                // double door
    g.add(M(box(0.04, 0.95, 0.5, 0, 0.1, D * 0.3), 0x5a3f26));
    for (const sx of [-1, 1]) g.add(M(box(W * 0.24, 0.7, 0.06, sx * W * 0.26, 0.5, D * 0.3), GLASS)); // vitrines
    // veranda posts + beam
    for (const sx of [-1, 1]) g.add(M(cyl(0.08, 0.09, 1.9, 6, sx * W * 0.42, 0, D * 0.42), BAMBOO));
    g.add(M(box(W * 0.92, 0.1, 0.14, 0, 1.85, D * 0.42), BAMBOO));
    // gift crate + trophy skull motif
    g.add(M(box(0.5, 0.5, 0.5, W * 0.28, 0.1, D * 0.55), CLAY, 0.7));
    g.add(M(box(0.56, 0.1, 0.1, W * 0.28, 0.5, D * 0.55), 0xf5d98a));
    signBoard(g, W, D, def.color, 1.9);
  },
  playground(def, W, D, g) {
    // thatched shade shelter + play structures on a sand pad (2x2)
    g.add(M(cyl(W * 0.6, W * 0.6, 0.08, 20), 0xf4e4b8));
    // shade shelter
    for (const sx of [-1, 1]) for (const sz of [-1, 1]) g.add(M(cyl(0.07, 0.08, 1.8, 6, sx * W * 0.34, 0, -D * 0.1 + sz * D * 0.28), BAMBOO));
    thatchGable(g, W * 0.8, D * 0.66, 0.7, 0, 1.9, -D * 0.1);
    // slide tower
    g.add(M(box(0.8, 1.0, 0.8, -W * 0.2, 0.1, D * 0.24), CLAY));
    thatchCone(g, 0.6, 0.5, -W * 0.2, 1.2, D * 0.24);
    const slide = box(0.4, 0.06, 1.4, 0, 0, 0); slide.applyMatrix4(new THREE.Matrix4().makeRotationX(0.55));
    slide.translate(-W * 0.2, 0.8, D * 0.24 + 0.9);
    g.add(M(slide, 0x8ecbe0, 0.4));
    // swing
    g.add(M(box(0.06, 1.3, 0.06, W * 0.12, 0, D * 0.28), BAMBOO));
    g.add(M(box(0.06, 1.3, 0.06, W * 0.44, 0, D * 0.28), BAMBOO));
    g.add(M(box(W * 0.36, 0.07, 0.07, W * 0.28, 1.3, D * 0.28), WOOD_D));
    g.add(M(box(0.22, 0.05, 0.13, W * 0.28, 0.7, D * 0.28), 0xe86a6a));
    // spring rider
    g.add(M(ball(0.16, W * 0.2, 0.5, -D * 0.3), 0x8fd88f));
  },
  petting(def, W, D, g) {
    // safari barn with thatch + paddock fence (2x2)
    g.add(M(box(W * 0.6, 1.4, D * 0.62, -W * 0.14, 0, -D * 0.05), CLAY));
    thatchGable(g, W * 0.68, D * 0.74, 1.05, -W * 0.14, 1.65, -D * 0.05);
    g.add(M(box(0.5, 0.85, 0.05, -W * 0.14, 0.1, D * 0.26), WOOD_D));       // barn door
    g.add(M(box(0.3, 0.3, 0.05, -W * 0.14, 1.35, D * 0.26), 0x3a2a20));     // loft window
    // hay + paddock
    g.add(M(box(0.42, 0.32, 0.32, W * 0.24, 0.04, -D * 0.24), 0xe8c98a));
    g.add(M(box(0.42, 0.32, 0.32, W * 0.3, 0.36, -D * 0.18), 0xdfba75));
    for (let i = 0; i < 4; i++) g.add(M(box(0.05, 0.45, 0.05, W * 0.02 + i * 0.28, 0.04, D * 0.34), WOOD));
    g.add(M(box(0.92, 0.05, 0.05, W * 0.44 - 0.46, 0.36, D * 0.34), WOOD));
    g.add(M(ball(0.14, W * 0.24, 0.3, D * 0.12), 0xe8dcc8));                // little goat
    g.add(M(ball(0.09, W * 0.34, 0.42, D * 0.12), 0xd9c4a8));
  },
  carousel(def, W, D, g) {
    // round pavilion with a big conical thatch roof over a carousel (2x2)
    g.add(M(cyl(W * 0.56, W * 0.6, 0.16, 16), CLAY));
    g.add(M(cyl(W * 0.52, W * 0.56, 0.06, 16, 0, 0.16, 0), 0xf5d98a));
    g.add(M(cyl(0.1, 0.12, 1.9, 8, 0, 0.2, 0), TEAK));                       // center mast
    const cols = [0xe86a6a, 0x8ecbe0, 0xf5d98a, 0xb7a8e8];
    for (let i = 0; i < 4; i++) {
      const a = i / 4 * Math.PI * 2 + 0.4;
      const px = Math.cos(a) * W * 0.34, pz = Math.sin(a) * W * 0.34;
      g.add(M(cyl(0.03, 0.03, 1.6, 6, px, 0.2, pz), BAMBOO));
      g.add(M(box(0.34, 0.18, 0.14, px, 0.78, pz), cols[i], 0.6));           // horse body
      g.add(M(box(0.1, 0.16, 0.1, px + 0.17, 0.92, pz), cols[i], 0.6));      // head
      for (const lx of [-0.11, 0.11]) g.add(M(box(0.04, 0.2, 0.04, px + lx, 0.58, pz), cols[i], 0.6));
    }
    thatchCone(g, W * 0.62, 1.1, 0, 2.0, 0);
    g.add(M(cyl(0.02, 0.02, 0.5, 4, 0, 3.1, 0), WOOD));
    g.add(M(box(0.3, 0.18, 0.02, 0.14, 3.45, 0), 0xe86a6a));                 // pennant
  },
  museum(def, W, D, g) {
    // grand safari lodge: stone base, timber colonnade, big thatch gable (3x2)
    g.add(M(box(W * 0.95, 0.18, D * 0.9, 0, 0, 0.04), STONE));
    g.add(M(box(W * 0.8, 0.14, D * 0.28, 0, 0, D * 0.38), 0xd9cbb8));        // steps
    g.add(M(box(W * 0.82, 1.9, D * 0.58, 0, 0.18, -D * 0.08), PLASTER));     // hall
    for (let i = 0; i < 6; i++) {                                            // timber colonnade
      const px = -W * 0.36 + i * W * 0.144;
      g.add(M(cyl(0.1, 0.12, 1.7, 7, px, 0.18, D * 0.22), TEAK));
      g.add(M(box(0.26, 0.1, 0.26, px, 1.88, D * 0.22), WOOD_D));
    }
    g.add(M(box(W * 0.9, 0.24, D * 0.68, 0, 2.0, -D * 0.02), WOOD_D));       // entablature
    thatchGable(g, W * 0.96, D * 0.72, 1.2, 0, 2.5, -D * 0.02);
    g.add(M(box(0.6, 1.0, 0.06, 0, 0.32, D * 0.2), 0x4a3626));               // tall doors
    // tusk arch motif over the entrance
    for (const sx of [-1, 1]) {
      const tusk = cyl(0.02, 0.08, 0.9, 6, sx * 0.32, 0.32, D * 0.24);
      tusk.applyMatrix4(new THREE.Matrix4().makeRotationZ(-sx * 0.5));
      g.add(M(tusk, 0xf0e6d0));
    }
    signBoard(g, W * 0.7, D, def.color, 1.2);
  },
  aquarium(def, W, D, g) {
    // long hall, thatch gable, big glass reef front + fish totem (3x2)
    g.add(M(box(W * 0.9, 1.6, D * 0.8, 0, 0, 0), 0x3c6a8a));
    g.add(M(box(W * 0.94, 0.12, D * 0.84, 0, 1.6, 0), PLASTER));
    thatchGable(g, W * 0.98, D * 0.9, 1.15, 0, 1.85, 0);
    g.add(M(box(W * 0.66, 1.0, 0.06, 0, 0.28, D * 0.41), GLASS, 0.2));       // reef glass
    g.add(M(box(W * 0.7, 0.1, 0.12, 0, 1.3, D * 0.41), WOOD_D));
    g.add(M(box(0.44, 0.85, 0.05, W * 0.34, 0.1, D * 0.41), 0x24404f));      // door
    for (const sx of [-1, 1]) g.add(M(cyl(0.16, 0.16, 0.06, 10, sx * W * 0.32, 0.9, -D * 0.41).rotateX(Math.PI / 2), GLASS, 0.2)); // portholes
    // fish totem on a bamboo pole
    g.add(M(cyl(0.04, 0.04, 1.1, 5, -W * 0.4, 1.7, 0), BAMBOO));
    const fish = ball(0.24, -W * 0.4, 3.0, 0); fish.scale(1.5, 0.85, 0.7);
    g.add(M(fish, 0xf2a45c, 0.5));
    g.add(M(cone(0.14, 0.24, 4, -W * 0.4 + 0.36, 2.92, 0).rotateZ(-1.2), 0xe8894a));
    // wave crest along the glass
    for (let i = 0; i < 6; i++) g.add(M(ball(0.09, -W * 0.32 + i * W * 0.128, 1.42, D * 0.41), 0x8ecbe0, 0.4));
  },
};

export const FACING_DIRS = {
  0: { x: 0, z: 1 },   // S / +z
  1: { x: 1, z: 0 },   // E / +x
  2: { x: 0, z: -1 },  // N / -z
  3: { x: -1, z: 0 },  // W / -x
};
export function facingAngle(facing) {
  const v = FACING_DIRS[facing] || FACING_DIRS[0];
  return Math.atan2(v.x, v.z);
}

const STAND_CACHE = new Map(); // type -> baked single mesh
const MID_CACHE = new Map();   // type -> baked base-only mesh (mid LOD, no dress-up props)
export const FOOTPRINTS = {};          // type -> [fw, fd] measured from the real mesh

// Far-distance billboard impostor: render the MID model once to a PNG, shown as a camera-facing
// Sprite instead of a plain block. One SHARED offscreen renderer (never one-per-type → no GL-context blowup).
const BILL_CACHE = new Map();   // type -> { tex, md }
let _amBakeR = null;
function _amBaker(S) {
  if (!_amBakeR) {
    _amBakeR = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
    _amBakeR.setPixelRatio(1); _amBakeR.outputColorSpace = THREE.SRGBColorSpace;
    _amBakeR.toneMapping = THREE.ACESFilmicToneMapping; _amBakeR.toneMappingExposure = 1.0;
    _amBakeR.setClearColor(0x000000, 0);
  }
  _amBakeR.setSize(S, S);
  return _amBakeR;
}
export function buildingImpostor(type, cellSize) {
  if (BILL_CACHE.has(type)) return BILL_CACHE.get(type);
  const S = 256, r = _amBaker(S);
  const scene = new THREE.Scene();
  scene.add(new THREE.HemisphereLight(0xeaf6f0, 0xcfe6c0, 0.8));
  scene.add(new THREE.AmbientLight(0xffffff, 1.1));
  const sun = new THREE.DirectionalLight(0xfff3e0, 1.5); sun.position.set(2, 4, 3); scene.add(sun);
  const model = buildAmenityModel(AMENITIES[type], cellSize, type);
  const box = new THREE.Box3().setFromObject(model), ctr = box.getCenter(new THREE.Vector3()), size = box.getSize(new THREE.Vector3());
  model.position.sub(ctr); scene.add(model);
  const md = Math.max(size.x, size.y, size.z) || 1;
  const cam = new THREE.OrthographicCamera(-md * 0.72, md * 0.72, md * 0.72, -md * 0.72, 0.1, 300);
  cam.position.set(md * 1.6, md * 1.0, md * 2.2); cam.lookAt(0, 0, 0);
  r.render(scene, cam);
  const cv = document.createElement('canvas'); cv.width = S; cv.height = S; cv.getContext('2d').drawImage(r.domElement, 0, 0);
  const tex = new THREE.CanvasTexture(cv); tex.colorSpace = THREE.SRGBColorSpace;
  const rec = { tex, md: md * 1.44 };
  BILL_CACHE.set(type, rec); return rec;
}

// Mid LOD: just the base building (GLB kit / fallback), baked to ONE mesh, WITHOUT the dress-up
// layer (palms, lamps, crates, flags, planters). A genuinely simpler mesh — no runtime decimation.
export function buildAmenityMid(def, cellSize, type) {
  if (!MID_CACHE.has(type)) {
    const baked = getBakedModel(type);
    if (!baked) {
      // Non-baked types (e.g. vet) have no dress-up layer — the full model IS already base-only,
      // so reuse it verbatim as the mid LOD. Guarantees identical size (no double-scale bug).
      MID_CACHE.set(type, buildAmenityModel(def, cellSize, type));
      return MID_CACHE.get(type).clone();
    }
    const g = new THREE.Group();
    g.add(baked);
    MID_CACHE.set(type, bakeGroup(g));
  }
  return MID_CACHE.get(type).clone();
}
// Baked-in default entrance side per building type (filled from the dev tool's output).
const DEFAULT_FRONTS = {};
const FRONT_BY_TYPE = Object.assign({}, DEFAULT_FRONTS); // type -> locked facing (0..3)

// Shared safari detailing applied to EVERY building — multiplies perceived detail.
function dressBuilding(g, def, W, D, type) {
  const round = ['candy', 'icecream', 'info', 'balloon'].includes(type);
  const acc = def.color;
  // 1. stone skirt / foundation edge (ring of small rocks around the base)
  const per = round ? 14 : 18;
  for (let i = 0; i < per; i++) {
    const a = i / per * Math.PI * 2;
    const rx = (round ? W * 0.46 : W * 0.5) + 0.04;
    const rz = (round ? W * 0.46 : D * 0.5) + 0.04;
    const x = Math.cos(a) * rx, z = Math.sin(a) * rz;
    if (!round && Math.abs(z - D * 0.5) < 0.3 && Math.abs(x) < W * 0.28) continue; // skip doorway front
    const r = 0.09 + (i % 3) * 0.04;
    g.add(M(ball(r, x, r * 0.4, z, 0), i % 2 ? 0xcdbcab : 0xb8a894));
  }
  // 2. potted palms flanking the front
  const potX = round ? W * 0.36 : W * 0.42;
  const potZ = (round ? W * 0.36 : D * 0.42);
  for (const sx of [-1, 1]) {
    const px = sx * potX, pz = potZ;
    const vase = vaseMesh(0, 0.5); if (vase) { vase.position.set(px, 0, pz); g.add(vase); } else { g.add(M(cyl(0.14, 0.11, 0.28, 8, px, 0, pz), CLAY, 0.8)); }
    const palm = potPalm();                                            // nice terrain palm, mini
    palm.scale.setScalar(0.5);
    palm.rotation.y = sx * 1.3;
    palm.position.set(px, 0.42, pz);
    g.add(palm);
  }
  // extra potted mini-trees at the back corners (pine + oak variety)
  for (const sx of [-1, 1]) {
    const px = sx * potX, pz = -potZ;
    const vase = vaseMesh(sx < 0 ? 1 : 2, 0.5); if (vase) { vase.position.set(px, 0, pz); g.add(vase); } else { g.add(M(cyl(0.14, 0.11, 0.28, 8, px, 0, pz), CLAY, 0.8)); }
    const tr = potTreeMesh(sx < 0 ? 'pine' : 'oak');
    tr.scale.setScalar(0.42); tr.rotation.y = sx * 2.0; tr.position.set(px, 0.42, pz);
    g.add(tr);
  }
  // 3. lamp from the lamp pack by the entrance
  const lx = round ? -W * 0.42 : -W * 0.5;
  const lamp = lampMesh(1.9);
  if (lamp) { lamp.position.set(lx, 0, potZ * 0.4); g.add(lamp); g.add(M(ball(0.09, lx, 1.6, potZ * 0.4), 0xfff2b0)); }
  else {
    g.add(M(cyl(0.03, 0.035, 1.5, 5, lx, 0, potZ * 0.4), WOOD_D));
    g.add(M(box(0.14, 0.2, 0.14, lx + 0.34, 1.24, potZ * 0.4), 0x4a3a2a));
    g.add(M(ball(0.07, lx + 0.34, 1.34, potZ * 0.4), 0xfff2b0));
  }
  // 4. painted tribal pattern band along the front wall base (row of colored triangles)
  if (!round) {
    const n = Math.max(5, Math.round(W / 0.34));
    for (let i = 0; i < n; i++) {
      const x = -W * 0.4 + i * (W * 0.8) / (n - 1);
      const tri = cone(0.09, 0.16, 3, x, 0.16, D * 0.5 + 0.02);
      g.add(M(tri, i % 2 ? acc : 0x3a2a20, 0.6));
    }
    g.add(M(box(W * 0.86, 0.04, 0.03, 0, 0.36, D * 0.5 + 0.02), 0x3a2a20)); // band line
  }
  // 5. small carved spirit totem to one side
  const tx = round ? W * 0.44 : W * 0.5 + 0.15, tz = -D * 0.3;
  g.add(M(cyl(0.1, 0.12, 0.9, 6, tx, 0, tz), 0x8a6d4a));
  g.add(M(ball(0.14, tx, 0.95, tz, 0), acc, 0.6));
  g.add(M(box(0.34, 0.06, 0.34, tx, 0.62, tz), 0x6a4a32));                 // outstretched arms
  g.add(M(cone(0.16, 0.2, 6, tx, 1.08, tz), 0x3a2a20));                    // headdress
  // 6. colorful bunting / flag garland strung HIGH so entrances stay clear
  {
    const cols = [0xd98f5a, 0xe8c86a, 0xc46a4a, 0xdba86a, 0xb5794a, 0xe0b878]; // warm savanna tones
    const span = round ? W * 0.82 : W * 0.94;
    const n = Math.max(6, Math.round(span / 0.34));
    const fy = 2.85;
    for (let i = 0; i < n; i++) {
      const t = i / (n - 1);
      const x = -span / 2 + span * t;
      const sag = Math.sin(t * Math.PI) * 0.16;
      g.add(M(box(0.14, 0.2, 0.02, x, fy - 0.2 - sag, (round ? W * 0.44 : D * 0.5) + 0.06), cols[i % cols.length], 0.6));
    }
    // support poles holding the garland up above the doorway
    for (const sx of [-1, 1]) g.add(M(cyl(0.04, 0.05, fy, 5, sx * span / 2, 0, (round ? W * 0.44 : D * 0.5) + 0.06), WOOD_D));
  }
  // 7. clay vases beside the door (replacing the old barrel/bowls)
  const bx = round ? W * 0.4 : W * 0.46, bz = round ? -W * 0.34 : D * 0.28;
  const vA = vaseMesh(3, 0.55); if (vA) { vA.position.set(bx, 0, bz); g.add(vA); }
  const vB = vaseMesh(4, 0.36); if (vB) { vB.position.set(bx - 0.32, 0, bz + 0.16); g.add(vB); }
  // 8. tall reed torches flanking the entrance path
  for (const sx of [-1, 1]) {
    const px = sx * (round ? W * 0.3 : W * 0.34), pz = (round ? W * 0.5 : D * 0.56);
    g.add(M(cyl(0.035, 0.05, 1.3, 5, px, 0, pz), 0x8a6d4a));
    g.add(M(cyl(0.09, 0.05, 0.16, 6, px, 1.28, pz), 0x5a3a24));            // bundle head
    g.add(M(cone(0.1, 0.24, 5, px, 1.42, pz), 0xffa53c, 0.4));            // flame
    g.add(M(cone(0.05, 0.12, 5, px, 1.56, pz), 0xffe08a, 0.3));           // flame tip
  }
  // 9. tufts of grass poking around the foundation
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2 + 0.5;
    const gx = Math.cos(a) * (round ? W * 0.5 : W * 0.54);
    const gz = Math.sin(a) * (round ? W * 0.5 : D * 0.54);
    for (let b = 0; b < 3; b++) {
      const bl = cone(0.03, 0.22, 3, gx + (b - 1) * 0.05, 0.05, gz);
      bl.applyMatrix4(new THREE.Matrix4().makeRotationZ((b - 1) * 0.3));
      bl.translate(0, 0, 0);
      g.add(M(cone(0.03, 0.22, 3, gx + (b - 1) * 0.06, 0.08, gz).applyMatrix4(new THREE.Matrix4().makeRotationZ((b - 1) * 0.3)), 0x7cb56a, 0.9));
    }
  }
  // 10. wooden crates (from the crate pack) + a woven basket beside the building
  const kx = round ? -W * 0.4 : -W * 0.48, kz = round ? W * 0.3 : D * 0.32;
  const c1 = crateMesh(0, 0.4); if (c1) { c1.position.set(kx, 0, kz); c1.rotation.y = 0.3; g.add(c1); } else { g.add(M(box(0.34, 0.34, 0.34, kx, 0, kz), 0xb5794a, 0.8)); }
  const c2 = crateMesh(1, 0.32); if (c2) { c2.position.set(kx + 0.06, c1 ? 0.4 : 0.34, kz - 0.04); c2.rotation.y = -0.4; g.add(c2); }
  g.add(M(cyl(0.16, 0.13, 0.26, 9, kx - 0.42, 0, kz + 0.1), 0xcaa06a, 0.85));   // basket
  g.add(M(cyl(0.14, 0.14, 0.05, 9, kx - 0.42, 0.26, kz + 0.1), 0x8fae5a));       // greens
  // 11. woven sun parasol on a pole to one side
  const ux = round ? W * 0.46 : W * 0.52, uz = round ? -W * 0.28 : -D * 0.24;
  g.add(M(cyl(0.035, 0.045, 2.0, 5, ux, 0, uz), WOOD_D));
  g.add(M(cone(0.85, 0.5, 8, ux, 1.95, uz), 0xcaa060, 0.85));
  g.add(M(cone(0.6, 0.12, 8, ux, 2.32, uz), 0xb5824a, 0.85));
  // 12. a couple of tall carved standing stones (savanna monoliths) at the back corners
  for (const sx of [-1, 1]) {
    const mx = sx * (round ? W * 0.4 : W * 0.5), mz = -D * 0.46;
    const hgt = 0.7 + (sx > 0 ? 0.25 : 0);
    g.add(M(box(0.22, hgt, 0.18, mx, 0, mz), 0xcdb98c, 0.9));
    g.add(M(box(0.26, 0.08, 0.22, mx, hgt, mz), acc, 0.7)); // painted cap
  }
  // 13. ground planter row of little aloe/agave clumps along the front
  for (let i = 0; i < (round ? 3 : 5); i++) {
    const px = -W * 0.32 + i * (W * 0.64) / (round ? 2 : 4);
    const pz = (round ? W * 0.5 : D * 0.5) + 0.28;
    for (let bl = 0; bl < 4; bl++) {
      const spk = cone(0.03, 0.3, 4, px, 0.02, pz);
      spk.applyMatrix4(new THREE.Matrix4().makeRotationZ((bl - 1.5) * 0.4));
      spk.applyMatrix4(new THREE.Matrix4().makeRotationY(bl * 1.4));
      spk.translate(px, 0.06, pz);
      g.add(M(spk, bl % 2 ? 0x6a9a52 : 0x88b566, 0.9));
    }
  }
  // 14. wooden bench along the left side
  {
    const bx = -(round ? W * 0.5 : W * 0.55), bz = 0;
    for (let s = 0; s < 4; s++) g.add(M(box(0.12, 0.05, 1.1, bx - 0.18 + s * 0.12, 0.42, bz), 0xa9855a)); // seat slats
    for (let s = 0; s < 3; s++) g.add(M(box(0.5, 0.05, 0.1, bx, 0.62 + s * 0.11, bz - 0.5), 0x9c7a50)); // backrest
    for (const lz of [-0.45, 0.45]) { g.add(M(box(0.5, 0.42, 0.08, bx, 0, bz + lz), 0x6a4a32)); }
    g.add(M(box(0.56, 0.06, 1.16, bx, 0.42, bz), 0x8a6d4a)); // seat frame
  }
  // 15. directional signpost (right-front)
  {
    const sx2 = (round ? W * 0.5 : W * 0.55), sz2 = (round ? W * 0.28 : D * 0.3);
    g.add(M(cyl(0.06, 0.07, 1.8, 6, sx2, 0, sz2), 0x8a6d4a));
    g.add(M(ball(0.09, sx2, 1.82, sz2), 0x6a4a32));
    const arrow = (y, dir, col) => { const b = box(0.62, 0.16, 0.05, dir > 0 ? 0.3 : -0.3, y, sz2); g.add(M(b, col, 0.7)); const tip = cone(0.12, 0.18, 3, dir > 0 ? 0.63 : -0.63, y + 0.08, sz2); tip.rotateZ(dir > 0 ? -Math.PI / 2 : Math.PI / 2); tip.translate(sx2, 0, 0); g.add(M(tip, col, 0.7)); };
    arrow(1.5, 1, acc); arrow(1.25, -1, 0xcaa060);
    g.add(M(box(0.66, 0.05, 0.05, sx2, 1.5, sz2), 0x6a4a32));
  }
  // 16. tapered bin (right-back)
  {
    const nx = (round ? W * 0.46 : W * 0.5), nz = -(round ? W * 0.2 : D * 0.22);
    g.add(M(cyl(0.16, 0.13, 0.42, 10, nx, 0, nz), 0x5f7a52));
    for (let r = 0; r < 2; r++) g.add(M(cyl(0.17, 0.17, 0.03, 10, nx, 0.1 + r * 0.2, nz), 0x47603c));
    g.add(M(cyl(0.17, 0.14, 0.07, 10, nx, 0.42, nz), 0x3a5030)); // rim
    g.add(M(ball(0.13, nx, 0.5, nz), 0x47603c)); // domed lid
  }
  // 17. striped parasol (back-left)
  {
    const ux = -(round ? W * 0.44 : W * 0.5), uz = -(round ? W * 0.26 : D * 0.28);
    g.add(M(cyl(0.04, 0.05, 2.2, 6, ux, 0, uz), 0x9c7a50));
    for (let s = 0; s < 8; s++) { const seg = cone(0.9, 0.55, 3, ux, 2.05, uz); seg.rotateY(s * Math.PI / 4); g.add(M(seg, s % 2 ? acc : 0xf0e2c2, 0.7)); }
    g.add(M(ball(0.08, ux, 2.62, uz), 0x6a4a32));
  }
  // 18. rope posts flanking the entrance path (front)
  for (const sx of [-1, 1]) {
    const rx = sx * (round ? W * 0.24 : W * 0.28), rz = (round ? W * 0.5 : D * 0.5) + 0.5;
    g.add(M(cyl(0.05, 0.06, 0.7, 6, rx, 0, rz), 0x8a6d4a));
    g.add(M(ball(0.08, rx, 0.72, rz), 0xcaa060));
  }
  {
    const rz = (round ? W * 0.5 : D * 0.5) + 0.5, rxA = -(round ? W * 0.24 : W * 0.28), rxB = (round ? W * 0.24 : W * 0.28);
    const rope = box(Math.abs(rxB - rxA), 0.03, 0.03, (rxA + rxB) / 2, 0.5, rz); g.add(M(rope, 0x6a4a32));
  }
  // 19. firewood pile (back-right)
  {
    const fx = (round ? W * 0.44 : W * 0.5), fz = -(round ? W * 0.4 : D * 0.42);
    for (let row = 0; row < 3; row++) for (let c = 0; c < 3 - row; c++) {
      const lg = cyl(0.06, 0.06, 0.6, 6, 0, 0, 0); lg.rotateZ(Math.PI / 2);
      lg.translate(fx, 0.06 + row * 0.11, fz - 0.28 + c * 0.14 + row * 0.07);
      g.add(M(lg, c % 2 ? 0xb28a62 : 0xc09a72));
    }
  }
  // 20. little stone lantern (left-front)
  {
    const lx2 = -(round ? W * 0.44 : W * 0.5), lz2 = (round ? W * 0.24 : D * 0.26);
    g.add(M(cyl(0.13, 0.15, 0.14, 6, lx2, 0, lz2), 0xcdbcab));
    g.add(M(cyl(0.06, 0.06, 0.5, 6, lx2, 0.14, lz2), 0xb8a894));
    g.add(M(box(0.22, 0.2, 0.22, lx2, 0.62, lz2), 0xd9cbb8));
    g.add(M(ball(0.07, lx2, 0.72, lz2), 0xfff2b0));
    g.add(M(cone(0.2, 0.16, 4, lx2, 0.82, lz2), 0xcdbcab));
  }
  // 21. hanging paper lanterns under the bunting
  {
    const span = round ? W * 0.82 : W * 0.94, y = 2.7, z = (round ? W * 0.44 : D * 0.5) + 0.06;
    const lc = [0xe8894a, 0xd9b34a, 0xc46a4a];
    for (let i = 0; i < 4; i++) { const x = -span / 2 + span * (i + 0.5) / 4; g.add(M(ball(0.11, x, y - 0.32, z), lc[i % 3], 0.6)); g.add(M(cyl(0.006, 0.006, 0.3, 3, x, y - 0.15, z), 0x3a2a20)); }
  }
  // 22. produce sacks by the crates
  {
    const sx3 = round ? -W * 0.28 : -W * 0.34, sz3 = round ? W * 0.34 : D * 0.36;
    for (let i = 0; i < 2; i++) { const s = ball(0.15, sx3 + i * 0.24, 0.12, sz3); s.scale(0.8, 1.1, 0.8); g.add(M(s, i ? 0xd9c48a : 0xc9a45c, 0.9)); g.add(M(ball(0.05, sx3 + i * 0.24, 0.28, sz3), 0x8a6d4a)); }
  }
  // 23. flower box along the front-right
  {
    const fx = (round ? W * 0.2 : W * 0.28), fz = (round ? W * 0.5 : D * 0.5) + 0.22;
    g.add(M(box(0.9, 0.16, 0.24, fx, 0, fz), 0x9c7a50));
    g.add(M(box(0.84, 0.06, 0.2, fx, 0.16, fz), 0x5c4630));
    const fc = [0xe86a6a, 0xf5d98a, 0xb7a8e8, 0xff9d80, 0x8ecbe0];
    for (let i = 0; i < 5; i++) { const bx2 = fx - 0.34 + i * 0.17; g.add(M(cyl(0.02, 0.02, 0.16, 4, bx2, 0.2, fz), 0x6a9a52)); g.add(M(ball(0.06, bx2, 0.38, fz), fc[i], 0.5)); }
  }
  // 24. welcome mat at the door
  g.add(M(box(0.7, 0.03, 0.4, 0, 0.01, (round ? W * 0.5 : D * 0.5) + 0.26), acc, 0.8));
  // 25. bird bath (front-left)
  {
    const bx = -(round ? W * 0.2 : W * 0.28), bz = (round ? W * 0.5 : D * 0.5) + 0.3;
    g.add(M(cyl(0.1, 0.13, 0.5, 8, bx, 0, bz), 0xcdbcab));
    g.add(M(cyl(0.28, 0.22, 0.1, 12, bx, 0.5, bz), 0xd9cbb8));
    g.add(M(cyl(0.24, 0.24, 0.03, 12, bx, 0.56, bz), 0x8ecbe0, 0.3));
  }
  // 26. scattered mushrooms + tuft accents around the base
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2 + 1.1;
    const mx = Math.cos(a) * (round ? W * 0.52 : W * 0.56), mz = Math.sin(a) * (round ? W * 0.52 : D * 0.56);
    g.add(M(cyl(0.03, 0.04, 0.1, 5, mx, 0, mz), 0xe8dcc8));
    g.add(M(ball(0.07, mx, 0.12, mz), i % 2 ? 0xc0392b : 0xd9762a));
    g.add(M(ball(0.015, mx + 0.03, 0.14, mz), 0xf0e6d0));
  }
}

// A visitor-scaled entrance door on the FRONT face (local +z) of any building.
function addDoor(g, W, D) {
  const fz = D / 2;
  const dw = 1.0, dh = 1.6;
  g.add(M(box(dw, dh, 0.2, 0, 0, fz - 0.06), 0x241a12));                 // recessed opening
  g.add(M(box(dw + 0.26, 0.2, 0.22, 0, dh, fz + 0.02), 0x8a5a2e));       // lintel
  g.add(M(box(dw + 0.44, 0.12, 0.24, 0, dh + 0.2, fz + 0.02), 0x9c6a3a));// cornice
  for (const sx of [-1, 1]) {
    g.add(M(box(0.16, dh + 0.2, 0.18, sx * (dw / 2 + 0.12), 0, fz), 0xcaa76a)); // jamb pilasters
    g.add(M(box(dw / 2 - 0.05, dh - 0.1, 0.05, sx * (dw / 4), 0.02, fz + 0.04), 0xa77038)); // door leaf
    g.add(M(ball(0.05, sx * 0.12, 0.85, fz + 0.08), 0xf5d98a));          // handle
  }
  g.add(M(box(dw + 0.5, 0.12, 0.5, 0, -0.05, fz + 0.16), 0xcbb994));     // threshold step
}

// Merge a whole building group (GLB + dress-up) into ONE vertex-coloured mesh (1 draw call).
function bakeGroup(group) {
  group.updateMatrixWorld(true);
  const geos = [];
  group.traverse((o) => {
    if (!o.isMesh || !o.geometry) return;
    const g = o.geometry.clone().applyMatrix4(o.matrixWorld);
    if (!g.attributes.normal) g.computeVertexNormals();
    if (!g.attributes.color) {
      const c = (o.material && o.material.color) || new THREE.Color(0xffffff);
      const n = g.attributes.position.count, arr = new Float32Array(n * 3);
      for (let i = 0; i < n; i++) { arr[i * 3] = c.r; arr[i * 3 + 1] = c.g; arr[i * 3 + 2] = c.b; }
      g.setAttribute('color', new THREE.BufferAttribute(arr, 3));
    }
    Object.keys(g.attributes).forEach((k) => { if (!['position', 'normal', 'color'].includes(k)) g.deleteAttribute(k); });
    geos.push(g.index ? g.toNonIndexed() : g);
  });
  if (!geos.length) return group;
  const merged = mergeGeometries(geos, false);
  const mesh = new THREE.Mesh(merged, new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.9 }));
  mesh.castShadow = true; mesh.receiveShadow = true;
  return mesh;
}

// Small buy-menu thumbnails for buildings, rendered front-forward (door toward camera).
export const AMENITY_THUMBS = {};
export function generateAmenityThumbnails(cellSize = 4) {
  if (Object.keys(AMENITY_THUMBS).length) { bus.emit('amenities:thumbs'); return; }
  const S = 128;
  const r = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  r.setSize(S, S); r.setPixelRatio(1);
  const scene = new THREE.Scene();
  scene.add(new THREE.HemisphereLight(0xffffff, 0x8899aa, 1.25));
  const dl = new THREE.DirectionalLight(0xfff0d8, 2.3); dl.position.set(3, 7, 6); scene.add(dl);
  const cam = new THREE.PerspectiveCamera(38, 1, 0.1, 400);
  for (const [key, def] of Object.entries(AMENITIES)) {
    let m; try { m = buildAmenityModel(def, cellSize, key); } catch (e) { continue; }
    const bb = new THREE.Box3().setFromObject(m);
    const size = bb.getSize(new THREE.Vector3());
    const ctr = bb.getCenter(new THREE.Vector3());
    const maxd = Math.max(size.x, size.y, size.z) || 1;
    m.position.sub(ctr);
    scene.add(m);
    const rad = maxd * 1.3;
    // door/front is local +z — camera on +z looks straight at the front
    cam.position.set(rad * 0.5, rad * 0.62, rad * 1.2); cam.lookAt(0, 0, 0);
    r.render(scene, cam);
    AMENITY_THUMBS[key] = r.domElement.toDataURL('image/png');
    scene.remove(m);
  }
  r.dispose();
  bus.emit('amenities:thumbs');
}

export function buildAmenityModel(def, cellSize, type) {
  if (!STAND_CACHE.has(type)) {
    // Dev-baked GLB for this type? Use it, with the dress-up layer sized to THIS model.
    const baked = getBakedModel(type);
    if (baked) {
      const g = new THREE.Group();
      g.add(baked);
      baked.updateMatrixWorld(true);
      const bb = new THREE.Box3().setFromObject(baked);
      const W = bb.max.x - bb.min.x, D = bb.max.z - bb.min.z;
      const dressG = new THREE.Group();
      dressBuilding(dressG, def, W, D, type);   // recalculated per building from its real size
      dressG.position.y = getBuildingSink(type); // keep dress-up on the ground even when the building is sunk
      g.add(dressG);
      const one = bakeGroup(g);   // fuse GLB + dress-up into a single mesh (performance)
      STAND_CACHE.set(type, one);
      return STAND_CACHE.get(type).clone();
    }
    const [fw, fd] = def.fp || [1, 1];
    const W = cellSize * fw * 0.86, D = cellSize * fd * 0.86;
    const g = new THREE.Group();
    if (type === 'entry') {
      // Battered block: LEFT/RIGHT slope inward going up; FRONT/BACK vertical. Door-wide, taller than visitors.
      const W = cellSize * 1.44, D = cellSize * 1.425, H = 2.3, t = H * 0.16; // 1.2× wider, 1.5× longer
      const b0 = [-W/2,0,D/2], b1 = [W/2,0,D/2], b2 = [W/2,0,-D/2], b3 = [-W/2,0,-D/2];
      const t0 = [-W/2+t,H,D/2], t1 = [W/2-t,H,D/2], t2 = [W/2-t,H,-D/2], t3 = [-W/2+t,H,-D/2];
      const tri = [
        b0,b1,t1, b0,t1,t0,   // front (vertical, straight)
        b1,b2,t2, b1,t2,t1,   // right (sloped)
        b2,b3,t3, b2,t3,t2,   // back (sloped)
        b3,b0,t0, b3,t0,t3,   // left (sloped)
        t0,t1,t2, t0,t2,t3,   // top
      ];
      const pos = new Float32Array(tri.length * 3);
      tri.forEach((v, i) => { pos[i*3]=v[0]; pos[i*3+1]=v[1]; pos[i*3+2]=v[2]; });
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
      geo.computeVertexNormals();
      const m = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({ color: 0xe4cf9e, roughness: 0.95, flatShading: true, side: THREE.DoubleSide }));
      m.castShadow = m.receiveShadow = true;
      g.add(m);
      // ---- refined entrance detailing ----
      const fz = D / 2;
      const dw = 0.98, dh = 1.95;
      // top cornice band around the whole roof edge
      g.add(M(box(W + 0.14, 0.16, D + 0.14, 0, H - 0.16, 0), 0xc2a877));
      g.add(M(box(W - 0.1, 0.1, D - 0.1, 0, H, 0), 0xd9c096));                 // roof cap slab
      // deep dark doorway + timber frame
      g.add(M(box(dw, dh, 0.2, 0, 0, fz - 0.08), 0x241a12));                   // recessed opening
      g.add(M(box(dw + 0.28, 0.22, 0.22, 0, dh, fz + 0.02), 0x8a5a2e));        // lintel beam
      g.add(M(box(dw + 0.5, 0.14, 0.26, 0, dh + 0.22, fz + 0.02), 0x9c6a3a));  // cornice over lintel
      // name plaque above the door
      g.add(M(box(dw + 0.1, 0.34, 0.06, 0, dh + 0.5, fz + 0.04), 0xe7d3a2, 0.6));
      g.add(M(box(dw - 0.2, 0.08, 0.07, 0, dh + 0.5, fz + 0.05), 0x8a5a2e));   // engraved line
      // double planked door leaves, flush
      for (const sx of [-1, 1]) {
        g.add(M(box(dw / 2 - 0.05, dh - 0.12, 0.05, sx * (dw / 4), 0.02, fz + 0.03), 0xa77038));
        for (let p = 0; p < 3; p++) g.add(M(box(0.03, dh - 0.24, 0.02, sx * (dw / 4) + (p - 1) * 0.1, 0.08, fz + 0.06), 0x8a5a2e));
        g.add(M(box(0.16, 0.1, 0.03, sx * (dw / 4), dh - 0.28, fz + 0.06), 0x8a5a2e)); // upper panel rail
        g.add(M(ball(0.05, sx * 0.13, 0.92, fz + 0.08), 0xf5d98a));            // handle
      }
      // square pilasters flanking the door, base + shaft + capital, full height
      for (const sx of [-1, 1]) {
        const cxp = sx * (dw / 2 + 0.34);
        g.add(M(box(0.28, 0.16, 0.28, cxp, 0, fz - 0.04), 0xc2a877));          // base
        g.add(M(box(0.22, H - 0.32, 0.22, cxp, 0.16, fz - 0.04), 0xd9c096));   // shaft
        g.add(M(box(0.3, 0.16, 0.3, cxp, H - 0.16, fz - 0.04), 0xc2a877));     // capital
        g.add(M(box(0.24, 0.06, 0.24, cxp, H - 0.5, fz - 0.02), 0x9c6a3a));    // decorative ring
      }
      // two-step threshold
      g.add(M(box(dw + 0.9, 0.12, 0.62, 0, -0.06, fz + 0.18), 0xcbb994));
      g.add(M(box(dw + 0.5, 0.1, 0.34, 0, 0.02, fz + 0.34), 0xdccaa0));
      g.add(M(box(dw + 0.7, 0.12, 0.5, 0, -0.05, fz + 0.14), 0xcbb994));      // threshold step
      STAND_CACHE.set(type, g);
      return STAND_CACHE.get(type).clone();
    }
    // Base: a real building from the sci-fi/desert GLB kit, tinted per type.
    const base = makeBuildingMesh(type, cellSize, [fw, fd], def.color);
    if (base) g.add(base);
    else { (BUILDERS[type] || BUILDERS.food)(def, W, D, g); } // fallback if kit not loaded
    // Dress-up layer disabled for now (helpers/props will all be redone once the
    // building+entrance meshes are finalized).
    // dressBuilding(g, def, W, D, type);
    g.scale.setScalar(2); // ×2 overall building scale (visual)
    const one = bakeGroup(g); // bake so it.model.scale === 1 (geometry holds the ×2), identical to the mid LOD
    one.receiveShadow = true;
    STAND_CACHE.set(type, one);
  }
  return STAND_CACHE.get(type).clone();
}

// Small building sized to a visitor (~1.8m tall) with a front door.
function entryBuilder(def, g) {
  g.add(M(box(1.5, 0.12, 1.3, 0, -0.06, 0), 0xcbb994));          // stoop
  g.add(M(box(1.3, 1.7, 1.1, 0, 0, 0), 0xe7c58a));              // walls
  g.add(M(box(1.36, 0.12, 1.16, 0, 1.7, 0), 0xd0a566));         // wall cap
  g.add(M(gable(1.4, 1.2, 0.6, 0, 1.82, 0), def.roofColor));    // little roof
  g.add(M(box(0.14, 0.5, 0.14, 0.42, 1.9, -0.25), 0x8f8577));   // chimney
  // door
  g.add(M(box(0.52, 1.0, 0.06, 0, 0.05, 0.55), 0x8a5a2e));      // door frame
  g.add(M(box(0.42, 0.9, 0.04, 0, 0.08, 0.58), 0xa77038));      // door
  g.add(M(ball(0.05, 0.13, 0.5, 0.61), 0xf5d98a));              // knob
  g.add(M(box(0.6, 0.16, 0.1, 0, 1.12, 0.55), 0x6a4a32));       // lintel
  g.add(M(box(0.5, 0.1, 0.06, 0, 1.24, 0.56), 0xffffff, 0.6));  // sign
  // windows
  for (const sx of [-1, 1]) g.add(M(box(0.28, 0.3, 0.04, sx * 0.42, 0.85, 0.55), 0xbfe3ea));
}

